import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/internal/Observable';
import { select, Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { tap } from 'rxjs/internal/operators/tap';
import { GatewayService } from 'src/assets/data/enum/gatewayService.enum';
import { AppState } from '../_reducers';
import { FarmWeatherLocation } from '../_models/farmWeatherLocation';
import { environment } from '../../../environments/environment';
import { selectAllFarmWeatherLocations } from './../_selectors/farmWeatherLocation.selectors';

@Injectable()
export class FarmWeatherLocationService {
  // Base URL for API requests
  private readonly gatewayBaseUrl = environment.apiGatewayUrl;

  // Path segment for the weather hub service
  private readonly gatewayServiceWeatherHub = GatewayService.WEATHER_HUB;

  // Full URL for weather hub endpoints
  private readonly weatherHubUrl = this.gatewayBaseUrl + this.gatewayServiceWeatherHub;

  // Observable holding the list of weather locations
  farmWeatherLocationList$: BehaviorSubject<FarmWeatherLocation[]> = new BehaviorSubject([]);

  constructor(
    private readonly http: HttpClient,
    private readonly store: Store<AppState>
  ) {
    // Subscribe to store updates and update the local BehaviorSubject
    this.store.pipe(
      select(selectAllFarmWeatherLocations),
      tap((locations: FarmWeatherLocation[]) => this.farmWeatherLocationList$.next(locations))
    );
  }

  /**
   * Fetches the list of weather locations for the selected company from the API.
   * Also updates the local BehaviorSubject with the result.
   *
   * @returns {Observable<FarmWeatherLocation[]>} Observable emitting the list of locations
   */
  getLocations(): Observable<FarmWeatherLocation[]> {
    const company = JSON.parse(localStorage.getItem('companySelected'));
    const params = new HttpParams().set('farm_id', company.id);
    return this.http
      .get<FarmWeatherLocation[]>(`${this.weatherHubUrl}/v1/locations/index`, { params })
      .pipe(tap((response: FarmWeatherLocation[]) => this.farmWeatherLocationList$.next(response)));
  }

  /**
   * Fetches a specific weather location by ID.
   *
   * @param {number} id - The ID of the location
   * @returns {Observable<FarmWeatherLocation>} Observable emitting the location
   */
  getLocation(id: number): Observable<FarmWeatherLocation> {
    return this.http.get<FarmWeatherLocation>(`${this.weatherHubUrl}/v1/locations/${id}`);
  }

  /**
   * Saves a new or existing weather location to the server.
   * If the location has an ID, it performs an update; otherwise, it creates a new record.
   *
   * @param {FarmWeatherLocation} location - The location object to save
   * @returns {Observable<FarmWeatherLocation>} Observable emitting the saved location
   */
  saveLocation(location: FarmWeatherLocation): Observable<FarmWeatherLocation> {
    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    location.farm_id = companySelected ? companySelected.id : null;

    return location.id
      ? this.http.put<FarmWeatherLocation>(
          `${this.weatherHubUrl}/v1/locations/${location.id}`,
          location
        )
      : this.http.post<FarmWeatherLocation>(`${this.weatherHubUrl}/v1/locations/create`, location);
  }

  /**
   * Deletes a weather location by its ID.
   *
   * @param {FarmWeatherLocation} location - The location object to delete
   * @returns {Observable<FarmWeatherLocation>} Observable emitting the deleted location (if returned)
   */
  deleteLocation(location: FarmWeatherLocation): Observable<FarmWeatherLocation> {
    return this.http.delete<FarmWeatherLocation>(
      `${this.weatherHubUrl}/v1/locations/${location.id}`
    );
  }
}
